import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Crown, Rocket, Heart, Cpu, Sparkles, CheckCircle, Clock, Users } from 'lucide-react';
import { GlowCard } from '../components/GlowCard';
import { LeadMagnet } from '../components/LeadMagnet';
import { growthSystems, packages, positioning } from '../data/servicesData';

const iconMap = {
  Brain: Brain,
  Crown: Crown,
  Rocket: Rocket,
  Heart: Heart,
  Cpu: Cpu,
};

const colorMap = {
  violet: 'from-violet-500 to-purple-600',
  emerald: 'from-emerald-500 to-teal-600',
  pink: 'from-pink-500 to-rose-600',
  amber: 'from-amber-500 to-orange-600',
  cyan: 'from-cyan-500 to-blue-600',
};

const colorBorderMap = {
  violet: 'border-violet-500/30 hover:border-violet-500/60',
  emerald: 'border-emerald-500/30 hover:border-emerald-500/60',
  pink: 'border-pink-500/30 hover:border-pink-500/60',
  amber: 'border-amber-500/30 hover:border-amber-500/60',
  cyan: 'border-cyan-500/30 hover:border-cyan-500/60',
};

export default function GrowthSystemsPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32" data-testid="growth-systems-page">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="text-xs font-mono text-violet-400 tracking-widest uppercase mb-4 block">
              Büyüme Sistemleri
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {positioning.oneLiner}
            </h1>
            <p className="text-xl text-zinc-400 leading-relaxed mb-8">
              {positioning.corePromise}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/50 rounded-full text-sm text-zinc-300">
                <Clock className="w-4 h-4 text-violet-400" />
                {positioning.workingModel}
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/50 rounded-full text-sm text-zinc-300">
                <Users className="w-4 h-4 text-emerald-400" />
                {positioning.ethicalFrame}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Growth Systems Grid */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase mb-4 block">
              5 Temel Sistem
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Büyüme Sistemlerimiz
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
              Her sistem, kendi içinde tamamlanmış bir büyüme motorudur. İhtiyacınıza göre tek tek veya birlikte çalışırlar.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {growthSystems.map((system, index) => {
              const IconComponent = iconMap[system.icon];
              return (
                <motion.div
                  key={system.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={`/buyume-sistemleri/${system.slug}`}>
                    <div className={`group relative h-full bg-[#0A0A0A] border ${colorBorderMap[system.color]} rounded-2xl p-8 transition-all duration-300 hover:bg-zinc-900/50`}>
                      {/* New Badge */}
                      {system.isNew && (
                        <span className="absolute top-4 right-4 px-3 py-1 text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full">
                          YENİ
                        </span>
                      )}
                      
                      {/* Icon */}
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colorMap[system.color]} p-0.5 mb-6`}>
                        <div className="w-full h-full rounded-2xl bg-[#0A0A0A] flex items-center justify-center">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="mb-6">
                        <span className="text-xs text-zinc-500 font-mono">{system.aka}</span>
                        <h3 className="text-xl font-bold text-white mt-1 mb-3 group-hover:text-violet-400 transition-colors">
                          {system.name}
                        </h3>
                        <p className="text-zinc-400 text-sm leading-relaxed">
                          {system.shortDescription}
                        </p>
                      </div>

                      {/* Best For */}
                      <div className="mb-6">
                        <span className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">İdeal:</span>
                        <ul className="space-y-1">
                          {system.bestFor.slice(0, 2).map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA */}
                      <div className="flex items-center gap-2 text-sm font-medium text-violet-400 group-hover:text-violet-300 transition-colors">
                        Sistemi Keşfet
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* aifabric Integration Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-900/20 via-[#121212] to-violet-900/20 border border-cyan-500/20 p-12">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50" />
              
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-white">aifabric</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Tüm Sistemlerin Ortak Zeka Katmanı
                  </h3>
                  <p className="text-zinc-400 leading-relaxed mb-6">
                    aifabric, her büyüme sisteminin içine entegre edilmiş yapay zeka katmanıdır. 
                    Veriyi içgörüye, içgörüyü aksiyona dönüştürür. Manuel analiz yerine, 
                    gerçek zamanlı optimizasyon sağlar.
                  </p>
                  <Link 
                    to="/aifabric" 
                    className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
                  >
                    aifabric Hakkında
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {[
                    'Gerçek zamanlı davranış analizi',
                    'Tahminsel performans skorları',
                    'Otomatik optimizasyon önerileri',
                    'Segment bazlı içgörüler',
                    'Anomali tespiti ve erken uyarı'
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-center gap-3 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800/50"
                    >
                      <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-cyan-400" />
                      </div>
                      <span className="text-zinc-300">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-pink-400 tracking-widest uppercase mb-4 block">
              Çalışma Modelleri
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Sprint Paketleri
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
              Proje bazlı çalışıyoruz. İhtiyacınıza göre hızlı sprint veya kapsamlı sistem kurulumu seçin.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-full z-10">
                    POPÜLER
                  </div>
                )}
                <GlowCard className={`p-8 h-full ${pkg.popular ? 'border-emerald-500/30' : ''}`}>
                  <div className="mb-6">
                    <span className={`inline-block px-3 py-1 text-xs font-mono bg-gradient-to-r ${colorMap[pkg.color]} bg-clip-text text-transparent border border-zinc-700 rounded-full mb-4`}>
                      {pkg.duration}
                    </span>
                    <h3 className="text-xl font-bold text-white mb-2">{pkg.name}</h3>
                    <p className="text-sm text-zinc-500">{pkg.idealFor}</p>
                  </div>
                  
                  <p className="text-zinc-400 text-sm mb-6">{pkg.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {pkg.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    to="/iletisim" 
                    className={`block w-full text-center py-3 rounded-xl font-medium transition-all ${
                      pkg.popular 
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:opacity-90' 
                        : 'border border-zinc-700 text-zinc-300 hover:border-violet-500/50 hover:text-white'
                    }`}
                  >
                    Görüşme Talep Et
                  </Link>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Magnet / Free Analysis Section */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <LeadMagnet />
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="methodology-card text-center max-w-3xl mx-auto"
          >
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Hangi Sistem Size Uygun?
              </h2>
              <p className="text-zinc-400 text-lg mb-10">
                15 dakikalık ücretsiz keşif görüşmesinde, mevcut durumunuzu analiz edip 
                size en uygun büyüme sistemini öneriyoruz.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/iletisim" 
                  className="btn-gradient flex items-center gap-2"
                  data-testid="growth-cta-primary"
                >
                  Ücretsiz Keşif Görüşmesi
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/portfolyo" 
                  className="btn-glow"
                  data-testid="growth-cta-secondary"
                >
                  Başarı Hikayelerini Gör
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
